/// <reference types="vite-plugin-svgr/client" />
import { type Message, colors, focusVisible, measures, strings, text } from "@quickpoint.me/shared";
import type { QpFile } from "@quickpoint.me/shared/src/gql/generated/schema";
import { useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import Countdown from "../../icons/countdown.svg?react";

import Trash from "../../icons/trash.svg?react";
import { spinAnimation } from "../../lib/animations";
import { HostId, UnprocessedFilesPresent } from "../../state/uploaderState";
import { StyledUploading } from "../uploadFiles/UploadFileView";

const StyledDetails = styled.div<{ $isInPortrait: boolean }>`
    position: relative;
    width: 100%;
    height: 100%;
    padding: 0.1rem;
    display: grid;
    font-family: ${(props) => props.theme.font};
    grid-template-rows: auto;
    grid-template-columns: ${(props) => (props.$isInPortrait ? "45% 45% 10%" : "40% 40% 20%")};
    align-items: center;
    justify-items: start;
    .trash-image {
        justify-self: end;
        cursor: pointer;
        background: none;
        border: none;
        padding: 0.2rem;
        min-width: 48px;
        min-height: 48px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        ${focusVisible}
    }
`;

const StyledFile = styled.div<{
	$dim: boolean;
	$imageUrl: string;
	$idx: number;
	$isInPortrait: boolean;
}>`
    font-family: ${(props) => props.theme.font};
    background-size: cover;
    overflow: hidden;
    opacity: ${(props) => (props.$dim ? "0" : "1")};
    position: relative;
    height: ${measures.dialogPanel.rowHeight};
    border-inline: 1px solid ${(props) => props.theme.navigationFrame};
    border-block-end: 1px solid ${(props) => props.theme.navigationFrame};
    padding-inline: ${(props) =>
			props.$isInPortrait
				? measures.dialogPanel.portrait.margin
				: measures.dialogPanel.landscape.margin};



    &:hover {
      background-color:${(props) => props.theme.stacksRollOverBackground};
    }

    .countdown {
        animation: ${spinAnimation};
        height: 50%;
        width: 50%;
        
    }

    .user-alt-text {
        color: ${colors.teal};
        font-style: italic;

    }

`;

function DataFileView({
	file,
	$idx,
	$isInPortrait,
}: { file: QpFile; $idx: number; $isInPortrait: boolean }) {
	const dimFile = useRecoilValue(UnprocessedFilesPresent); // make the uploaded files appear lighter when new files are being uploaded.
	const toAuthorBC = useMemo(
		() => new BroadcastChannel(strings.broadcastChannel.author),
		[],
	);
	const [deleting, setDeleting] = useState(false);
	const hostId = useRecoilValue(HostId);

	const trash = () => {
		setDeleting(true);
		const message: Message = {
			hostId,
			type: "fileUploader",
			action: strings.messages.deleteImage,
			payload: file.id,
		};
		toAuthorBC.postMessage(message);
	};

	return (
		<StyledFile
			$dim={dimFile}
			data-testid="styled-data-file"
			$imageUrl={file.url}
			className="file file-grid file-drop-zone"
			$idx={$idx}
			$isInPortrait={$isInPortrait}
		>
			<StyledDetails $isInPortrait={$isInPortrait}>
				<span className="user-defined-name">{file.userDefinedName}</span>
				<span className="user-alt-text alt-text">{file.altText}</span>
				<button
					type="button"
					onClick={() => trash()}
					aria-label={`${text.controls.trashImage}: ${file.userDefinedName}`}
					className="file-control trash-image"
				>
					<Trash aria-hidden="true" focusable="false" />
				</button>
			</StyledDetails>

			{deleting && (
				<StyledUploading>
					<Countdown className="countdown" />
				</StyledUploading>
			)}
		</StyledFile>
	);
}

export default DataFileView;
